import styled, { css } from 'styled-components';
import { Flexbox, FlexboxVertical } from '../../styles/Layouts';
import { getTheme, getThemeColor, getThemeFont } from '../../styles/Theme';
import React, { FC, useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { PopUpProps, PopUpState, PopUpStateProps } from '../../utils/state/PopUpState';
import { JobProps } from '../../utils/interfaces/Jobs';
import { LinkHandler } from '../common/Link';
import { BREAKPOINT_LG, BREAKPOINT_MD, BREAKPOINT_SM } from '../../styles/General';
import { FontStyleMomentumRegular, TextBase, TextBaseBold, TEXTS } from '../../styles/FontStyles';
import { useCurrentUser } from '../../utils/state/UserSessionState';
import { ClockIcon } from '../icons/ClockIcon';
import { StrapiImage } from '../common/StrapiImage';
import { ProductProps } from '../../utils/interfaces/Product';
import axios from 'axios';
import { JobFilterState } from '../../utils/state/JobFilter';
import { parseDate } from '../../utils/format/parseDate';
import { FEATURE_JOB_AD_CLICK_STATISTICS_ENABLED } from '../../FeatureConfig';
import { useCart } from '../../utils/hooks/useCart';
import { useGA } from '../../utils/tracking/useGA';
import { parsePrice } from '../../utils/format/parsePrice';
import { useHistory } from 'react-router-dom';
import { BasicCardWrapperStyle } from '../common/Card';
import { ShareMenu } from '../navigation/ShareMenu';
import { FlatrateProps } from '../../utils/interfaces/Flatrate';
import { LoadingSpinner } from '../common/LoadingSpinner';

export const HighlightTag = styled.p`
  position: absolute;
  top: 0;
  right: 0;
  background: ${getTheme(({ colors }) => colors.purple)};
  padding: 0 .5rem .125rem .75rem;
  border-bottom-left-radius: 1rem;
  color: ${getTheme(({ colors }) => colors.green)};
  transition: background-color .15s ease-out, color .15s ease-out;
`;

const ImmutableStyledLink = styled(LinkHandler).attrs(({ disabled = false }: any) => ({ tabIndex: disabled ? -1 : 0 }))<{ disabled?: boolean }>`
  ${TextBaseBold};
  color: ${getTheme(({ colors }) => colors.purple)};
  padding: 1rem;
  width: 100%;
  text-align: center;

  &:not(:last-of-type) {
    border-bottom: 1px solid var(--color-element-boarder-card);
  }

  ${BREAKPOINT_MD} {
    width: auto;
    border-bottom: 0;
    border-right: 1px solid var(--color-element-boarder-card);
  }

  :hover {
    color: ${getThemeColor('green')};
    background: ${getThemeColor('purple')};
  }
  
  ${({ disabled = false }) => disabled && css`
    color: ${getThemeColor('darkGray')};
    pointer-events: none;
  `};
`;

const FooterWrapper = styled(Flexbox)`
  position: relative;
  border-top: 1px solid var(--color-element-boarder-card);
  justify-content: space-between;
  align-items: center;
`;

const DesktopStatistics = styled(FlexboxVertical)`
  display: none;
  padding: 1.5rem;
  white-space: nowrap;
  border-left: 1px solid ${getThemeColor('green')};

  > :first-child {
    margin-bottom: .5rem;
    font-weight: bold;
  }

  ${BREAKPOINT_MD} {
    display: initial;
  }
`;

const ShareMenuWrapper = styled.div`
  margin-bottom: 2rem;
`;

const RenewJobAdButton = styled.button`
  font-family: ${getThemeFont('default')};
  font-weight: bold;
  font-size: 1rem;
  line-height: 1.45;
  color: ${getThemeColor('purple')};
  background: none;
  appearance: none;
  border: none;
  height: 100%;
  border-bottom: 1px solid var(--color-element-boarder-card);
  padding: 1rem;
  width: 100%;

  :hover {
    color: ${getThemeColor('green')};
    background: ${getThemeColor('purple')};
    cursor: pointer;
  }

  ${BREAKPOINT_MD} {
    border-bottom: 0;
    border-right: 1px solid var(--color-element-boarder-card);
    width: auto;
  }
`;

const JobAdPreviewWrapper = styled(FlexboxVertical)<{ active: boolean, highlighted: boolean }>`
  ${BasicCardWrapperStyle};

  ${({ highlighted }) => highlighted && css`
    border-width: .375rem;
  `};

    //background: ${({ active }) => active ? 'white' : getTheme(({ colors }) => colors.gray)};
  transition: border-color .15s ease-out;

  &:hover {
    ${({ active = true }) => active && css`
      border-color: ${getTheme(({ colors }) => colors.pink)};

      ${ImmutableStyledLink}, ${FooterWrapper} {
        border-color: ${getTheme(({ colors }) => colors.pink)};
      }

      ${HighlightTag} {
        background: ${getTheme(({ colors }) => colors.pink)};
        color: ${getTheme(({ colors }) => colors.purple)};
      }

      ${DesktopStatistics} {
        border-color: ${getTheme(({ colors }) => colors.pink)};
      }

      ${RenewJobAdButton} {
        border-color: ${getTheme(({ colors }) => colors.pink)};
      }
    `};
  }
`;

const InfoWrapper = styled(FlexboxVertical)<{ showInPortal?: boolean }>`
  position: relative;

  ${({ showInPortal = false }) => showInPortal ? BREAKPOINT_MD : BREAKPOINT_SM} {
    flex-direction: row;
  }
`;

const LinkWrapper = styled(FlexboxVertical)`
  align-items: center;
  width: 100%;

  ${BREAKPOINT_MD} {
    flex-direction: row;

    > :not(:last-child) {
      border-bottom: none;
      border-right: 1px solid var(--color-element-boarder-card);
    }
  }
`;

const JobDetailLinkWrapper = styled(LinkHandler)<{ active: boolean }>`
  pointer-events: ${({ active }) => active ? 'all' : 'none'};
`;

const JobContentWrapper = styled(Flexbox)`
  position: relative;
  justify-content: space-between;
  align-items: stretch;
`;

const MainContentWrapper = styled(FlexboxVertical)`
  width: 100%;
`;

const CompanyTitle = styled.p`
  ${TextBaseBold};

  ${BREAKPOINT_SM} {
    display: none;
  }
`;

const DesktopCompanyTitle = styled(CompanyTitle)`
  display: none;
  margin-bottom: 1rem;

  ${BREAKPOINT_MD} {
    display: flex;
  }
`;

const DescriptionWrapper = styled(FlexboxVertical)<{ active: boolean }>`
  color: ${({ active }) => active ? '' : getTheme(({ colors }) => colors.darkGray)};
  padding: 1rem;

  ${BREAKPOINT_SM} {
    padding: 1.5rem;
  }
`;

const JobLocationWrapper = styled(Flexbox)`
  flex-wrap: wrap;
  margin: auto 0 .25rem;
`;

const JobTitle = styled.h2`
  ${TEXTS.headline4};
  margin-bottom: 1rem;
`;

const InfoHeaderWrapper = styled(Flexbox)`
  padding: 1rem 1rem 0;

  ${BREAKPOINT_SM} {
    padding: 0;
  }
`;

const ImageWrapper = styled.div<{ active: boolean, placeholderImage: boolean }>`
  position: relative;
  width: 4rem;
  height: 4rem;
  flex-shrink: 0;

  ${({ active }) => !active && css`
    filter: grayscale(100%);
  `};

  margin-right: 1rem;

  ${BREAKPOINT_SM} {
    width: 12.5rem;
    height: 12.5rem;
    margin-right: 0;
  }

  ${({ placeholderImage }) => placeholderImage && css`
    display: none;

    ${BREAKPOINT_MD} {
      display: initial;
    }
  `};

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ImagePlaceholder = styled(Flexbox)`
  ${FontStyleMomentumRegular};
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${getThemeColor('gray')};
  color: ${getThemeColor('pink')};
  justify-content: center;
  text-align: center;
  overflow: hidden;
  word-break: break-word;
  border: 1rem solid transparent;
  font-size: 1rem;

  display: none;

  ${BREAKPOINT_SM} {
    display: flex;
  }
`;

const MobileNotificationWrapper = styled(Flexbox)<{ active: boolean }>`
  justify-content: center;
  color: ${({ active }) => active ? getThemeColor('darkGray') : getThemeColor('black')};
  margin: 2rem 1rem 0 1rem;

  && {
    border: none;
  }

  ${BREAKPOINT_MD} {
    display: none;
  }

  p {
    margin-left: .5rem;
    ${TextBase};
  }
`;

const MobileExpiryDate = styled(MobileNotificationWrapper)`
  margin: 1rem -1rem 0 -1rem;
  padding: 1rem 0 1rem 1rem;
  border-top: 1px solid var(--color-element-boarder-card);
  border-bottom: 1px solid var(--color-element-boarder-card);
  justify-content: flex-start;

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }
`;

const MobileClickTracking = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1rem;

  > :first-child {
    margin-bottom: .5rem;
    font-weight: bold;
  }

  ${BREAKPOINT_SM} {
    border-top: 1px solid var(--color-element-boarder-card);
    margin: 1rem -1.5rem 0 -1.5rem;
    padding: 1rem 1.5rem 0;
  }

  ${BREAKPOINT_MD} {
    display: none;
  }
`;

const DesktopNotificationWrapper = styled(Flexbox)<{ active: boolean }>`
  display: none;
  white-space: nowrap;
  margin-right: 1rem;
  color: ${({ active }) => active ? getThemeColor('darkGray') : getThemeColor('black')};

  ${BREAKPOINT_MD} {
    display: flex;
  }

  svg {
    margin-right: .5rem;
  }
`;

const JobAdIdHintMobile = styled.p`
  ${TEXTS.small};
  color: ${getThemeColor('darkGray')};
  margin-top: 1rem;

  ${BREAKPOINT_LG} {
    display: none;
  }
`;

const JobAdIdHintDesktop = styled.p`
  ${TEXTS.small};
  color: ${getThemeColor('darkGray')};
  margin: 0 1rem;

  display: none;
  visibility: hidden;

  ${BREAKPOINT_LG} {
    display: block;
    visibility: visible;
  }
`;

const ExternalLogo = styled.img`
  position: absolute;

  && {
    width: 1.5rem;
    height: 1.5rem;
    bottom: -.5rem;
    right: -.5rem;
    top: auto;
    left: auto;
  }

  object-fit: contain;
  border-radius: 50%;
  border: 2px solid #fff;

  ${BREAKPOINT_MD} {
    && {
      width: 2rem;
      height: 2rem;
      bottom: -.75rem;
      right: -1rem;
    }
  }
`;

interface JobAdPreviewProps {
  job: JobProps;
  product?: ProductProps;
  renewAddOn?: ProductProps | null;
  availableFlatrate?: FlatrateProps;
  showInPortal?: boolean;
  onDelete?: () => void;
  onPublish?: () => void;
}

export const JobAdPreview: FC<JobAdPreviewProps> = (props) => {
  const {
    showInPortal = false,
    job,
    product,
    renewAddOn = null,
    availableFlatrate,
    onDelete,
    onPublish,
  } = props;

  const { userSessionState } = useCurrentUser();
  const setPopUpState = useSetRecoilState<PopUpStateProps>(PopUpState);

  const filterState = useRecoilValue(JobFilterState);
  const expireTime = Math.ceil((parseDate(job.expiryDate).getTime() - Date.now()) / (1000 * 3600 * 24));
  const active = !!((showInPortal ? expireTime > 0 : true) || job?.savedAsDraft);
  const history = useHistory();

  const seoFilters = (() => {
    if (!showInPortal) {
      const occupationFilters = filterState.occupationTypes.filter(({ selected }) => selected).map((filter) => encodeURIComponent(filter.name)).join(',');
      const careerLevelFilters = filterState.careerLevels.filter(({ selected }) => selected).map((filter) => encodeURIComponent(filter.name)).join(',');
      const benefitsFilters = filterState.benefits.filter(({ selected }) => selected).map((filter) => encodeURIComponent(filter.name)).join(',');
      const categories = filterState.categories.filter(({ selected }) => selected).map((filter) => encodeURIComponent(filter.name)).join(',');
      return `occupation=${occupationFilters}&careerLevel=${careerLevelFilters}&benefits=${benefitsFilters}&categories=${categories}`;
    }
    return '';
  })();

  const { addToCart, setCartVisibility } = useCart();
  const { trackEvent } = useGA();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPopUpState((state) => {
      if (state.popUp) {
        return {
          popUp: {
            ...state.popUp,
            loading,    // update popup loading state
          },
        }
      }
      return state;
    });
  }, [loading]);

  const flatrateContainsRenewal = (
    !!renewAddOn && !!job.flatRateId && !!availableFlatrate &&
     availableFlatrate.products.some(({ id }) => id.toString() === renewAddOn.id.toString())
  );

  const renewJobAd = async () => {
    if (renewAddOn) {
      if (flatrateContainsRenewal) {   // renew within flatrate
        setLoading(true);
        return axios.post(`/api/customer/${userSessionState?.user?.id}/job-ads/initialize-job-posting-renewal`, {
          flatrateId: availableFlatrate.id,
          productId: renewAddOn.id,
          jobAdId: job.id,
        })
          .then((response) => {
            setLoading(false);
            published();    // update job ads page
          })
          .catch((error) => {
            setLoading(false);
            console.log('RENEW FLATRATE JOB AD ERROR', error?.response?.status, error?.response?.data);
          });
      } else {    // add addon "renewal" to cart
        trackEvent({
          category: 'checkout',
          action: `added offer ${renewAddOn.name} (${parsePrice(renewAddOn.price, 'EUR', true)}) with id ${renewAddOn.id} to cart`,
        });

        // add item to cart
        setLoading(true);
        return addToCart({ quantity: 1, id: renewAddOn.id, extraInfo: { jobAdId: job.id } })
          .then((response) => {
            setLoading(false);
            setCartVisibility(true);    // open cart flyout
          })
          .catch((error) => {
            setLoading(false);
            console.log(error);
          });
      }
    }
  };

  const renewPopUpContent: PopUpProps = {
    headline: 'Stellenanzeige verlängern',
    text: `
      Anzeige: ${job.title}<br>
      Paket: ${renewAddOn?.appliesToProducts.find(({ productType }) => productType === 'NORMAL')?.name || ''}<br>
      Aktuelle Frist: ${parseDate(job?.expiryDate).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })}<br>
      Dauer der Verlängerung: ${renewAddOn?.runtime}<br>
    `,
    buttons: {
      declineText: 'abbrechen',
      declineAction: () => setPopUpState({ popUp: null }),
      acceptText: ({ loading }) => loading
        ? <LoadingSpinner style={{ minWidth: '5rem' }} inline color={'white'} />
        : flatrateContainsRenewal ? 'verlängern' : 'in den warenkorb',
      acceptAction: async () => {
        await renewJobAd();
        setPopUpState({ popUp: null });
      },
    },
  };

  const deletePopUpContent = {
    headline: 'Löschen der Anzeige',
    text: 'Bist du sicher, dass du die Anzeige löschen willst?',
    buttons: {
      declineText: 'abbrechen',
      declineAction: () => setPopUpState({ popUp: null }),
      acceptText: 'anzeige löschen',
      acceptAction: () => {
        setPopUpState({ popUp: null });
        if (onDelete) {
          onDelete();
        }
      },
    },
  };

  const sharePopUpContent = {
    headline: 'Anzeige teilen',
    text: `Teile deine Anzeige auf deinen Social Media Kanälen für mehr Aufmerksamkeit.`,
    renderChildren: () => <ShareMenuWrapper>
      <ShareMenu
        type={'job'}
        title={job?.title}
        company={job?.company}
        slug={job?.slug}
      />
    </ShareMenuWrapper>,
    buttons: {
      declineText: 'abbrechen',
      declineAction: () => setPopUpState({ popUp: null }),
    },
  };

  const publishPopUpContent: PopUpProps = {
    headline: 'Veröffentlichung der Anzeige',
    text: `Deine Anzeige wird ab sofort für ${product?.runtime || 30} Tage veröffentlicht.`,
    buttons: {
      declineText: 'abbrechen',
      declineAction: () => setPopUpState({ popUp: null }),
      acceptText: ({ loading }) => loading
        ? <LoadingSpinner style={{ minWidth: '5rem' }} inline color={'white'} />
        : 'veröffentlichen',
      acceptAction: async () => {
        setLoading(true);
        await saveJobAd().then(r => {
          setLoading(false);
          published();
          history.push(`/portal/job/${job?.id}/preview/success`);
        }).catch(e => {
          setLoading(false);
          console.log(e);
        });
        setPopUpState({ popUp: null });
      },
    },
  };

  const published = () => {
    if (onPublish) {
      onPublish();
    }
  };

  const saveJobAd = async () => {
    const { titleImage, ...payload } = job || {};
    return await axios.patch(`/api/customer/${userSessionState?.user?.id}/job-ads/${job?.id}`, {
      ...payload,
      published: true,
      savedAsDraft: false,
    });
  };

  const jobUrl = (() => {
    if (showInPortal && !job.published) {
      return `/portal/job/${job.id}${seoFilters ? `?${seoFilters}/preview` : '/preview'}`;
    }
    return `/job/${job.slug}${seoFilters ? `?${seoFilters}` : ''}`;
  })();

  return (
    <JobAdPreviewWrapper active={active} highlighted={job.highlighted && !showInPortal}>
      <JobContentWrapper>
        <MainContentWrapper>
          {
            ((job.highlighted && !showInPortal) || (showInPortal && product)) &&
            <HighlightTag>
              {
                showInPortal
                  ? product?.name?.toUpperCase()
                  : 'TOP JOB'
              }
            </HighlightTag>
          }

          <JobDetailLinkWrapper
            active={active}
            external={!showInPortal || job.published}
            href={jobUrl}
            onClick={() => {
              if (job && !showInPortal) {
                // internal tracking
                axios.post(`/api/job-ad/${job.id}/click`, { type: 'click' })
                  .then(() => {
                    // console.log('INTERNAL TRACKING', 'tracked click for job ad', job.id, job.title);
                  })
                  .catch((err) => {
                    // console.log('INTERNAL TRACKING', 'error tracking click for job ad', job.id, job.title, err);
                  });
              }
            }}
          >
            {/* tracking clicks */}
            <InfoWrapper showInPortal={showInPortal && FEATURE_JOB_AD_CLICK_STATISTICS_ENABLED}>
              <InfoHeaderWrapper>
                <ImageWrapper
                  active={active}
                  placeholderImage={showInPortal ? !userSessionState.user?.logo : !job.customerLogoUrl}
                >
                  {
                    showInPortal
                      ? userSessionState.user?.logo
                        ? <StrapiImage image={userSessionState.user?.logo} alt={'Firmenlogo'} sizes={'12rem'} />
                        : (
                          <ImagePlaceholder>
                            {
                              (job?.company || '').length > 50
                                ? job?.company?.slice(0, 50) + '...'
                                : job?.company
                            }
                          </ImagePlaceholder>
                        )
                      :
                      (job?.companyLogoUrl || job?.customerLogoUrl)
                        ?
                        <img src={job?.companyLogoUrl || job?.customerLogoUrl || undefined}
                             alt={`${job.company} logo`} />
                        : (
                          <ImagePlaceholder>
                            {
                              (job?.company || '').length > 50
                                ? job?.company?.slice(0, 50) + '...'
                                : job?.company
                            }
                          </ImagePlaceholder>
                        )
                  }
                  {
                    job?.companyLogoUrl && job?.customerLogoUrl && <ExternalLogo src={job.customerLogoUrl} />
                  }
                </ImageWrapper>

                <CompanyTitle>{job?.company}</CompanyTitle>
              </InfoHeaderWrapper>

              <DescriptionWrapper active={active}>
                <DesktopCompanyTitle>{job?.company}</DesktopCompanyTitle>
                <JobTitle>{job?.title}</JobTitle>
                {
                  (job?.locations || []).length > 0 &&
                  <JobLocationWrapper>
                    {
                      job?.locations?.map((location, index, array) => (
                          <p key={index}>
                            {location.name}
                            {index < array.length - 1 && <> |&nbsp;</>}
                          </p>
                        ),
                      )
                    }
                  </JobLocationWrapper>
                }
                {
                  job.firstPublishedAt &&
                  <p>Veröffentlicht: {parseDate(job?.firstPublishedAt).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}</p>
                }
                {
                  showInPortal &&
                  <>
                    <JobAdIdHintMobile>Anzeigen-Nr. für Support-Anfragen: {job?.id}</JobAdIdHintMobile>

                    <MobileExpiryDate active={active}>
                      <ClockIcon />
                      <p>{expireTime > 0 ? `Anzeige läuft in ${expireTime} ${expireTime === 1 ? 'Tag' : 'Tagen'} ab` : 'Diese Anzeige ist abgelaufen'}</p>
                    </MobileExpiryDate>

                    {/* tracking clicks */}
                    {
                      FEATURE_JOB_AD_CLICK_STATISTICS_ENABLED &&
                      <MobileClickTracking>
                        <p>Statistiken</p>
                        <p>Klicks Anzeigen: {job.trackingClicks || 0}</p>
                        <p>Klicks Bewerben Button: {job.trackingApplyClicks || 0}</p>
                      </MobileClickTracking>
                    }
                  </>
                }
              </DescriptionWrapper>
            </InfoWrapper>
          </JobDetailLinkWrapper>
        </MainContentWrapper>

        {
          // tracking clicks
          showInPortal && FEATURE_JOB_AD_CLICK_STATISTICS_ENABLED &&
          <DesktopStatistics>
            <p>Statistiken</p>
            <p>Klicks Anzeigen: {job.trackingClicks || 0}</p>
            <p>Klicks Bewerben Button: {job.trackingApplyClicks || 0}</p>
          </DesktopStatistics>
        }
      </JobContentWrapper>

      {
        showInPortal &&
        <FooterWrapper>
          <LinkWrapper>
            {
              renewAddOn && !job.savedAsDraft &&
              <RenewJobAdButton onClick={(e) => {
                e.preventDefault();
                setPopUpState({ popUp: renewPopUpContent });
              }}>
                {expireTime <= 0 ?
                  'Reaktivieren' :
                  'Verlängern'
                }
              </RenewJobAdButton>
            }

            <ImmutableStyledLink href={`/portal/job/${job?.id}`}>
              {active ? 'Bearbeiten' : 'Ansehen'}
            </ImmutableStyledLink>

            <ImmutableStyledLink href={'#'} onClick={(e) => {
              e.preventDefault();
              setPopUpState({ popUp: deletePopUpContent });
            }}>
              Löschen
            </ImmutableStyledLink>

            {
              (expireTime > 0 && job.published) &&
              <ImmutableStyledLink href={'#'} onClick={(e) => {
                e.preventDefault();
                setPopUpState({ popUp: sharePopUpContent });
              }}>
                Teilen
              </ImmutableStyledLink>
            }

            {
              job.savedAsDraft &&
              <ImmutableStyledLink
                href={'#'}
                disabled={!!job.flatRateId && !availableFlatrate}
                onClick={(e) => {
                  e.preventDefault();
                  setPopUpState({ popUp: publishPopUpContent });
                }}
              >
                {
                  (!!job.flatRateId && !availableFlatrate) ? 'Flatrate inaktiv' : 'Veröffentlichen'
                }
              </ImmutableStyledLink>
            }

            <JobAdIdHintDesktop>Anzeigen-Nr. für Support-Anfragen: {job?.id}</JobAdIdHintDesktop>
          </LinkWrapper>
          {
            !job.savedAsDraft &&
            <DesktopNotificationWrapper active={active}>
              <ClockIcon /> {expireTime > 0 ? `Anzeige läuft in ${expireTime} ${expireTime === 1 ? 'Tag' : 'Tagen'} ab` : 'Diese Anzeige ist abgelaufen'}
            </DesktopNotificationWrapper>
          }
        </FooterWrapper>
      }
    </JobAdPreviewWrapper>
  );
};
